import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../store";
import { axiosApi, downloadFile as downloadFileHelper, getPostfixWithRobotId, getResponseError, handleErrorRTKAndAxiosAsync } from "@/helpers";
import { ENDPOINTS_SES, URL_SES } from "@/constants/api_endpoints";
import { IDownloadFileProps, ISesState, IUploadFileProps, IUploadFileResponse } from "@/types/ses/sesTypes";
import { IResponse, RequestStatus, ResponseStatus } from "@/types/statusTypes";

const initialState: ISesState = {
	debuggerSession: null,

	uploadFile: {
		id: null,
		progress: 0,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	downloadFile: {
		url: null,
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// загрузка файла
export const uploadFile = createAsyncThunk("ses/uploadFile", async ({ robotId, formData }: IUploadFileProps, { dispatch, rejectWithValue }) => {
	try {
		const response: AxiosResponse<IUploadFileResponse> = await axiosApi.post(
			`${URL_SES}/${ENDPOINTS_SES.UPLOAD_FILE}/${robotId}`,
			{
				file: formData.get("file"),
			},
			{
				headers: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (progressEvent) => {
					if (progressEvent.progress) {
						const progress = Math.round(progressEvent.progress * 100);
						dispatch(changeProgressUploadFile(progress));
					}
				},
			}
		);
		return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

// скачивание файла
export const downloadFile = createAsyncThunk("ses/downloadFile", async ({ robotId, fileId, fileName }: IDownloadFileProps, { getState, rejectWithValue }) => {
	try {
		const response: AxiosResponse<Blob | IResponse> = await axiosApi.get(
			`${URL_SES}/${ENDPOINTS_SES.DOWNLOAD_FILE}/${getPostfixWithRobotId((getState() as RootState).sesRobot.robotList.activeRobotVersion, robotId)}/${fileId}`,
			{
				responseType: "blob",
			}
		);
		if (response.data instanceof Blob) {
			downloadFileHelper(response.data, fileName);
		} else return response.data;
	} catch (error) {
		const handledError = await handleErrorRTKAndAxiosAsync(error);
		return rejectWithValue(handledError);
	}
});

const sesSlice = createSlice({
	name: "ses",
	initialState,
	reducers: {
		// добавление данных сессии чат-виджета
		addSessionDataFromChatWidget: (state, action: PayloadAction<string>) => {
			state.debuggerSession = JSON.parse(action.payload);
		},
		// изменение прогресса загрузки файла в %
		changeProgressUploadFile: (state, action: PayloadAction<number>) => {
			state.uploadFile.progress = action.payload;
		},
		// -------------------------------------------------------------------------
		// очистка state
		clearState: (state) => {
			state.debuggerSession = initialState.debuggerSession;

			state.uploadFile = initialState.uploadFile;
			state.downloadFile = initialState.downloadFile;
		},
		// очистка данных сессии отладчика
		clearDebuggerSession: (state) => {
			state.debuggerSession = initialState.debuggerSession;
		},
		// очистка данных загузки файла
		clearUploadFile: (state) => {
			state.uploadFile = initialState.uploadFile;
		},
		// очистка данных скачивания файла
		clearDownloadFile: (state) => {
			state.downloadFile = initialState.downloadFile;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(uploadFile.pending, (state) => {
				state.uploadFile.status = RequestStatus.LOADING;
			})
			.addCase(uploadFile.fulfilled, (state, action) => {
				if (action.payload) {
					state.uploadFile.status = RequestStatus.IDLE;
					state.uploadFile.error = action.payload.error;
					state.uploadFile.message = action.payload.message;
					if ("id" in action.payload && action.payload.id) state.uploadFile.id = action.payload.id;
				} else state.uploadFile.status = RequestStatus.FAILED;
			})
			.addCase(uploadFile.rejected, (state, action: PayloadAction<unknown>) => {
				state.uploadFile.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.uploadFile.error = error;
				state.uploadFile.message = message;
			})
			.addCase(downloadFile.pending, (state) => {
				state.downloadFile.status = RequestStatus.LOADING;
			})
			.addCase(downloadFile.fulfilled, (state, action) => {
				state.downloadFile.status = RequestStatus.IDLE;
				if (action.payload) {
					state.downloadFile.error = action.payload.error;
					state.downloadFile.message = action.payload.message;
				}
			})
			.addCase(downloadFile.rejected, (state, action: PayloadAction<unknown>) => {
				state.downloadFile.status = RequestStatus.FAILED;
				const { error, message } = getResponseError(action);
				state.downloadFile.error = error;
				state.downloadFile.message = message;
			});
	},
});

export const { addSessionDataFromChatWidget, changeProgressUploadFile, clearState, clearDebuggerSession, clearUploadFile, clearDownloadFile } = sesSlice.actions;

export const selectDebuggerSession = (state: RootState) => state.ses.debuggerSession;

export const selectUploadFile = (state: RootState) => state.ses.uploadFile;
export const selectDownloadFile = (state: RootState) => state.ses.downloadFile;

export default sesSlice.reducer;
